import { Menu, MenuItem } from '@/component/Menu';
import Popper from '@/component/Popper';
import Translate from '@/component/Translate';
import { useTexts } from '@/feature/lang';
import type { Translation } from '@/feature/lang/types';
import { Icon } from '@/icon/Icon';
import { Button, Divider } from '@mui/material';
import { useMemo } from 'react';
import { useAggregation } from './AggregationContext';
import type { AggregationType } from './types';

export const AggregationAction = () => {
  const aggregation = useAggregation();
  const texts = useTexts().component.table;

  if (aggregation === undefined) {
    return null;
  }

  return (
    <Popper
      placement="bottom-start"
      Trigger={({ onClick }) => (
        <Button onClick={onClick} color="grey" variant="contained">
          <Translate text={texts.actions.aggregations} />
        </Button>
      )}
      Content={Content}
    />
  );
};

interface AggregationTypeViewInfo {
  id: AggregationType;
  label: Translation;
}

const useTypes = () => {
  const texts = useTexts().component.table.aggregations;
  const aggregation = useAggregation();

  return useMemo(() => {
    const types: AggregationTypeViewInfo[] = [
      {
        id: 'avg' as const,
        label: texts.avg,
      },
      {
        id: 'min',
        label: texts.min,
      },
      {
        id: 'max',
        label: texts.max,
      },
      {
        id: 'sum',
        label: texts.sum,
      },
    ];

    return types.filter((type) => aggregation?.types.includes(type.id));
  }, [texts, aggregation?.types]);
};

const Content = ({ onClose }: PopperContentProps) => {
  const aggregation = useAggregation()!;
  const types = useTypes();
  const texts = useTexts().component.table;

  return (
    <Menu>
      {types.map((type) => (
        <MenuItem
          onClick={() => {
            aggregation.toggleShowType(type.id);
            onClose();
          }}
          icon={
            <Icon
              name={
                aggregation.showTypes.includes(type.id)
                  ? 'check_box'
                  : 'check_box_outline_blank'
              }
            />
          }
          key={type.id}
        >
          <Translate text={type.label} />
        </MenuItem>
      ))}
      <Divider />
      <MenuItem
        onClick={() => {
          aggregation.setShowOnlyAggregations(
            !aggregation.showOnlyAggregations
          );
          onClose();
        }}
        icon={
          <Icon
            name={
              aggregation.showOnlyAggregations
                ? 'check_box'
                : 'check_box_outline_blank'
            }
          />
        }
      >
        <Translate text={texts.actions.showOnlyAggregations} />
      </MenuItem>
    </Menu>
  );
};
